import { useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import { SecuritySelect } from '@ovComponents/3-pattern/securitySelect/securitySelect';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { currencySymbol, isCurrencyCode } from '@utils/currency';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography } from 'hephaestus/web';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadProductAumExposure from './downloadProductAumExposure';
import {
  type ExposureLevel,
  ProductAumExposureTable,
} from './productAumExposureTable';
import { FETCH_PRODUCT_AUM_EXPOSURE } from './queries';

const PAGE_SIZE = 15;

export type ProductAumExposureOptions = {
  customTitle?: { en?: string; fr?: string; es?: string };
  exposureLevel?: ExposureLevel;
  enableDownload?: boolean;
};

export const ProductAumExposure = ({
  options = {},
}: {
  options?: ProductAumExposureOptions;
}) => {
  const { t } = useTranslation(['homePageConfiguration', 'shared']);
  const canReadApiExports = usePermission('read:api_exports');
  const { activeOrganization, baseCurrency } = useContext(UserContext);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const [financialProductId, setFinancialProductId] = useState<string>('');
  const [selectedTicker, setSelectedTicker] = useState<string>('');
  const [selectedProductName, setSelectedProductName] = useState<string>('');
  const [fetchedFilterKey, setFetchedFilterKey] = useState('');

  const exposureLevel: ExposureLevel = options.exposureLevel ?? 'HOUSEHOLD';
  const hasProductSelected = !!financialProductId;
  const filterKey = `${financialProductId}:${exposureLevel}:${baseCurrency}:${activeOrganization.id}`;

  const showDownloadButton =
    canReadApiExports && options.enableDownload !== false && hasProductSelected;

  const { loading, data, previousData } = useQuery(FETCH_PRODUCT_AUM_EXPOSURE, {
    skip: !hasProductSelected,
    variables: {
      input: {
        filter: {
          financialProductId,
          objectType: exposureLevel,
          organizationId: activeOrganization.id,
          currency: baseCurrency,
        },
        pagination: {
          offSet: (page - 1) * perPage,
          perPage,
          sortField: 'marketValueCents',
          sortDesc: true,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (data?.fetchProductAumExposure) {
      setFetchedFilterKey(filterKey);
    }
  }, [data, filterKey]);

  const isDataForCurrentQuery = fetchedFilterKey === filterKey;
  const isPreviousDataForCurrentQuery = !!previousData && isDataForCurrentQuery;
  const response =
    (isDataForCurrentQuery ? data : undefined) ??
    (isPreviousDataForCurrentQuery ? previousData : undefined);
  const totalCount = Number(response?.fetchProductAumExposure?.totalCount ?? 0);
  const totalMarketValueCents = Number(
    response?.fetchProductAumExposure?.totalMarketValueCents ?? 0,
  );
  const exposures = response?.fetchProductAumExposure?.exposures ?? [];
  const pageCount = Math.max(1, Math.ceil(totalCount / perPage));
  const showPagination =
    pageCount > 1 || page > 1 || exposures.length === perPage;
  const showPreviousData = isPreviousDataForCurrentQuery;
  const showSummary = hasProductSelected && totalCount > 0;

  const formattedTotalMarketValue = useMemo(() => {
    const symbol = isCurrencyCode(baseCurrency)
      ? (currencySymbol[baseCurrency] ?? '$')
      : '$';
    return `${formatMoneyValue(
      totalMarketValueCents,
      symbol,
      2,
      true,
      true,
    )} ${baseCurrency}`;
  }, [baseCurrency, totalMarketValueCents]);

  const levelLabel = t(
    `homePageConfiguration:productAumExposure.exposureLevels.${exposureLevel}_${totalCount === 1 ? 'one' : 'other'}`,
  );

  const exportFilter = {
    financialProductId,
  };

  return (
    <Card loading={loading && !showPreviousData}>
      {options.customTitle && (
        <CardContent sx={{ pb: 0 }}>
          <Typography variant='titleLarge'>
            {translateBackend(options.customTitle)}
          </Typography>
        </CardContent>
      )}
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 280, flex: 1 }}>
          <SecuritySelect
            value={financialProductId}
            setSecurity={(security) => {
              if (!security || security === 'ANY') {
                setFinancialProductId('');
                setSelectedTicker('');
                setSelectedProductName('');
                setFetchedFilterKey('');
              } else if (security.id) {
                setFetchedFilterKey('');
                setFinancialProductId(security.id);
                setSelectedTicker(security.ticker?.trim() ?? '');
                setSelectedProductName(
                  translateBackend(security.translatedName),
                );
              }
              setPage(1);
            }}
            label={t('homePageConfiguration:productAumExposure.security')}
            organizationId={activeOrganization.id}
            clearable
          />
        </Box>
        {showDownloadButton && (
          <DownloadProductAumExposure
            filter={exportFilter}
            exposureLevel={exposureLevel}
            ticker={selectedTicker}
          />
        )}
      </CardContent>

      {!hasProductSelected ? (
        <Typography variant='bodyMedium' sx={{ px: 2, pb: 3, fontWeight: 500 }}>
          {t('homePageConfiguration:productAumExposure.selectSecurity')}
        </Typography>
      ) : (
        <>
          {showSummary && (
            <Typography
              variant='bodyMedium'
              sx={{ px: 2, pb: 2, fontWeight: 500 }}
            >
              {t('homePageConfiguration:productAumExposure.summary', {
                productName: selectedProductName,
                ticker: selectedTicker,
                totalMarketValue: formattedTotalMarketValue,
                count: totalCount,
                levelLabel,
              })}
            </Typography>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <ProductAumExposureTable
              exposures={exposures}
              exposureLevel={exposureLevel}
              loading={loading}
              hasPreviousData={showPreviousData}
              baseCurrency={baseCurrency}
            />
          </Box>
          {showPagination && (
            <Box sx={{ px: 2, py: 1 }}>
              <Pagination
                size='small'
                page={page}
                total={totalCount}
                perPage={perPage}
                count={Math.max(pageCount, page)}
                onChange={(_event, newPage) => setPage(newPage)}
                onChangePerPage={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </>
      )}
    </Card>
  );
};
